import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, MessageCircle, Phone, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { API_BASE, formatApiErrorDetail } from "@/api";
import { SectionHead } from "@/components/SectionHead";

export const QuoteSection = () => {
  const { t, lang } = useLang();
  const { contact, waLink } = useSite();
  const q = t.quote;
  const [form, setForm] = useState({ name: "", school: "", city: "", students: "", phone: "", role: "principal", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/quote`, { ...form, students: Number(form.students), lang });
      setSent(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || q.err);
    } finally {
      setBusy(false);
    }
  };

  const field = (label, testid, node) => (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-ink">{label}</span>
      {node}
    </label>
  );

  return (
    <section id="quote" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead kicker={q.kicker} title={q.title} sub={q.sub} />
        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-[2rem] border border-cardline bg-white p-8 sm:p-10 lg:col-span-7"
            data-testid="quote-form-card"
          >
            {sent ? (
              <div className="grid min-h-[380px] place-items-center text-center" data-testid="quote-form-success">
                <div>
                  <CheckCircle2 className="mx-auto h-14 w-14 text-pine" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-ink sm:text-3xl">{q.successTitle}</h3>
                  <p className="mt-2 text-inksoft">{q.successBody}</p>
                  <button
                    onClick={() => { setSent(false); setForm({ name: "", school: "", city: "", students: "", phone: "", role: "principal", message: "" }); }}
                    className="mt-7 rounded-full border-2 border-pine px-6 py-3 font-bold text-pine transition-colors hover:bg-pine hover:text-cream"
                  >
                    {q.successAgain}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="quote-form">
                {field(q.name, null, <input required minLength={2} data-testid="quote-form-name-input" className="field-input" placeholder={q.namePh} value={form.name} onChange={set("name")} />)}
                {field(q.school, null, <input required minLength={2} data-testid="quote-form-school-input" className="field-input" placeholder={q.schoolPh} value={form.school} onChange={set("school")} />)}
                {field(q.city, null, <input required minLength={2} data-testid="quote-form-city-input" className="field-input" placeholder={q.cityPh} value={form.city} onChange={set("city")} />)}
                {field(q.students, null, <input required type="number" min="1" data-testid="quote-form-quantity-input" className="field-input" placeholder="250" value={form.students} onChange={set("students")} />)}
                {field(q.phone, null, <input required type="tel" dir="ltr" data-testid="quote-form-phone-input" className="field-input" placeholder="+20 1XX XXX XXXX" value={form.phone} onChange={set("phone")} />)}
                {field(q.role, null, (
                  <select data-testid="quote-form-role-select" className="field-input" value={form.role} onChange={set("role")}>
                    {Object.entries(q.roles).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                ))}
                <div className="sm:col-span-2">
                  {field(q.message, null, <textarea rows={3} data-testid="quote-form-message-input" className="field-input resize-none" placeholder={q.msgPlaceholder} value={form.message} onChange={set("message")} />)}
                </div>
                {err && <p data-testid="quote-form-error" className="rounded-xl bg-terra-light px-4 py-3 text-sm font-semibold text-terra sm:col-span-2">{err}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="quote-form-submit-button"
                  className="flex items-center justify-center gap-2.5 rounded-full bg-pine px-7 py-4 font-bold text-cream transition-colors hover:bg-pine-dark disabled:opacity-60 sm:col-span-2"
                >
                  <Send className="h-4 w-4" />
                  {busy ? q.sending : q.submit}
                </button>
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-5 lg:col-span-5"
          >
            <div className="flex-1 rounded-[2rem] border border-sun/50 bg-sun-light p-8 sm:p-10">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-terra text-white">
                <MessageCircle className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold text-ink">{q.waTitle}</h3>
              <p className="mt-2 leading-relaxed text-inksoft">{q.waBody}</p>
              <a
                data-testid="whatsapp-direct-order-button"
                href={waLink(lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 flex items-center justify-center gap-2.5 rounded-full bg-terra px-7 py-4 font-bold text-white transition-colors hover:bg-terra-dark"
              >
                <MessageCircle className="h-5 w-5" />
                {q.waBtn}
              </a>
            </div>

            <div className="rounded-[2rem] border border-cardline bg-white p-8">
              <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-inkmute">{q.contact}</h4>
              <a href={`mailto:${contact.email}`} className="mt-4 flex items-center gap-3 font-semibold text-ink transition-colors hover:text-pine" data-testid="contact-email-link">
                <Mail className="h-5 w-5 text-pine" />
                <span dir="ltr">{contact.email}</span>
              </a>
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="mt-3 flex items-center gap-3 font-semibold text-ink transition-colors hover:text-pine" data-testid="contact-phone-link">
                <Phone className="h-5 w-5 text-pine" />
                <span dir="ltr">{contact.phone}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
